import { useEffect, useState } from 'react'
import { ChevronRight } from '../../components/ChevronRight'
import { GameTitle, type GameId } from '../../components/GameTitle'
import { SectionTitle } from '../../components/SectionTitle'
import { ScreenHeader } from '../../components/ScreenHeader'
import { BackgroundTitle } from '../../components/BackgroundTitle'
import { getCardById } from '../../games/ideatthat/data/ingredientCards'
import { daysLeftInWeek, getWeeklyPeriods, type WeeklyPeriods, fetchWeeklyCardOptions, type WeeklyCardOption } from '../../lib/api/weekly'
import { useAuth } from '../../lib/auth'
import { useT } from '../../i18n'
import { fetchMyActiveRooms, type ActiveRoom } from '../../lib/api/activeRooms'
import { ActiveRoomCard } from '../../components/ActiveRoomCard'
import { fetchRulesIndex } from '../rules/rulesApi'
import { LivingRulebookScreen } from '../rules/LivingRulebookScreen'
import type { NavTab, Profile } from '../types'

/**
 * Hub cards whose Rules pill opens the game's LIVING RULEBOOK from
 * the rules API (owner call, 2026-08-07: living books over printed
 * PDFs wherever one exists). All five rule-bearing cards are in the
 * API now — IET and Three Step went live 2026-08-07, which retired
 * the bundled RulebookScreen. Accents match the Rules tab's colors.
 */
const LIVING_RULES: Record<string, { slug: string; accent: string }> = {
  ideatthat:     { slug: 'id-eat-that',          accent: '#B58615' },
  colorful:      { slug: 'colorful',             accent: '#C9B785' },
  three_step:    { slug: 'three-step-solution',  accent: '#0EAE93' },
  tokyo_metro:   { slug: 'tokyo-metro',          accent: '#F79C20' },
}

interface Props {
  profile: Profile
  /** Open the full Your Game Rooms list (shown when more than 4 are live). */
  onOpenMyRooms: () => void
  onNavigate: (tab: NavTab) => void
  onPlayIdEatThat: () => void
  onPlayColorful: () => void
  onPlayTokyoMetro: () => void
  onPlayThreeStep: () => void
  onPlayPortableParty: () => void
  onPlayFakeOutEndless: () => void
  onPlayCloseEnoughEndless: () => void
  onWeeklyEvent: (game: 'iet' | 'colorful' | 'three_step') => void
  onOpenPaywall: () => void
}

interface GameCard {
  id: string
  title: string
  /** If set, renders the boxed wordmark treatment matching the product. */
  wordmark?: GameId
  /**
   * Colours the plain-text title for games with no `wordmark` art.
   * Defaults to `text-brand-text`.
   */
  titleClass?: string
  tagline: string
  /** Box art. Omit for games with no art yet — the card renders a tinted tile. */
  image?: string
  /**
   * House style: every game card uses the SAME pair of CSS variables —
   * `bg-{game}-light` for the fill (game colour mixed 50% white) and
   * `border-{game}-dark` for the stroke (mixed 50% black). See
   * `tailwind.config.ts` `colors.game.*` for the precomputed values.
   */
  fillClass: string
  strokeClass: string
  available: boolean
}

const GAMES: GameCard[] = [
  {
    id: 'ideatthat',
    title: "I'D EAT THAT",
    wordmark: 'ideatthat',
    tagline: 'A 3-course storytelling game',
    image: '/games/ideatthat.webp',
    // IET surfaces use the gold gradient utility instead of the
    // flat cream — keep this in sync with .event-card / btn-iet.
    fillClass: 'iet-gold',
    strokeClass: 'border-game-iet-dark',
    available: true,
  },
  {
    id: 'colorful',
    title: 'COLORFUL',
    wordmark: 'colorful',
    tagline: 'Match the subject to its color',
    image: '/games/colorful.webp',
    fillClass: 'bg-game-colorful-light',
    strokeClass: 'border-game-colorful-dark',
    available: true,
  },
  {
    id: 'three_step',
    title: 'THREE STEP SOLUTION',
    wordmark: 'three-step',
    tagline: 'Combine two actions, write three steps · Weekly + live',
    image: '/games/three-step.webp',
    fillClass: 'bg-game-three-step-light',
    strokeClass: 'border-game-three-step-dark',
    available: true,
  },
  {
    // 50 party games, each a rules page plus notes saved to your account.
    // No boxed wordmark art exists, so this falls back to the plain title
    // tinted with the game's green. TestFlight preview: live with one
    // filled game (06 SARDINES); the other 49 slots are COMING SOON rows
    // inside the list screen.
    id: 'portable_party',
    title: 'PORTABLE PARTY GAMES',
    titleClass: 'text-game-portable-party-dark',
    tagline: '50 games from around the world that you can play anywhere with no components',
    image: '/games/portable-party.webp',
    fillClass: 'bg-game-portable-party-light',
    strokeClass: 'border-game-portable-party-dark',
    available: true,
  },
  {
    // The endless, replayable cuts of the two daily word games. The
    // Weekly versions stay free; these are subscriber-gated and dealt
    // from a pool with a per-account seen ledger, so nothing repeats.
    id: 'fake_out_endless',
    title: 'FAKE OUT',
    titleClass: 'text-game-fakeout-dark',
    tagline: 'Spot the real definition · Solo, pass and play, or private rooms',
    image: '/games/fake-out.webp',
    fillClass: 'bg-game-fakeout-light',
    strokeClass: 'border-game-fakeout-dark',
    available: true,
  },
  {
    id: 'close_enough_endless',
    title: 'CLOSE ENOUGH',
    titleClass: 'text-game-closeenough-dark',
    tagline: 'Hunt the secret word by meaning · Solo, pass and play, or private rooms',
    image: '/games/close-enough.webp',
    fillClass: 'bg-game-closeenough-light',
    strokeClass: 'border-game-closeenough',
    available: true,
  },
  {
    id: 'tokyo_metro',
    title: 'TOKYO METRO',
    wordmark: 'tokyo-metro',
    tagline: 'Route planning on the real Tokyo subway · Online match',
    image: '/games/tokyo-metro.webp',
    fillClass: 'bg-game-tokyo-metro-light',
    strokeClass: 'border-game-tokyo-metro-dark',
    available: false,
  },
]

export function HubScreen({
  profile, onNavigate,
  onPlayIdEatThat, onPlayColorful, onPlayTokyoMetro, onPlayThreeStep,
  onPlayPortableParty, onPlayFakeOutEndless, onPlayCloseEnoughEndless, onWeeklyEvent,
  onOpenPaywall, onOpenMyRooms,
}: Props) {
  const t = useT()
  // Map game id → click handler. Games without an entry stay "Soon".
  const handlers: Record<string, () => void> = {
    ideatthat: onPlayIdEatThat,
    colorful: onPlayColorful,
    three_step: onPlayThreeStep,
    tokyo_metro: onPlayTokyoMetro,
    portable_party: onPlayPortableParty,
    fake_out_endless: onPlayFakeOutEndless,
    close_enough_endless: onPlayCloseEnoughEndless,
  }

  // Live weekly-event preview for the banner. We only need this week's
  // shared ingredients + the days-left countdown — full data lives on
  // the Weekly tab. Fetched lazily; the banner renders a neutral
  // placeholder until we have data.
  const { configured, session } = useAuth()

  // Rooms the player is seated in that are still running. This is the
  // way back into an async match when the "your turn" push was
  // dismissed or notifications are off; without it the only re-entry
  // was remembering the room code. Guests always read empty.
  const [activeRooms, setActiveRooms] = useState<ActiveRoom[]>([])
  // Bumped after a Leave on a card so the list refetches immediately
  // instead of waiting for the next hub mount.
  const [roomsTick, setRoomsTick] = useState(0)
  useEffect(() => {
    if (!session) { setActiveRooms([]); return }
    let cancelled = false
    fetchMyActiveRooms()
      .then(rows => { if (!cancelled) setActiveRooms(rows) })
      .catch(() => { /* offline; the hub still works without the list */ })
    return () => { cancelled = true }
  }, [session, roomsTick])
  const [periods, setPeriods] = useState<WeeklyPeriods | null>(null)
  useEffect(() => {
    if (!configured) return
    let cancelled = false
    getWeeklyPeriods()
      .then(p => { if (!cancelled) setPeriods(p) })
      .catch(() => { /* banner falls back to placeholder */ })
    return () => { cancelled = true }
  }, [configured])

  const sharedNames = periods
    ? periods.write.shared_ingredients
        .map(id => getCardById(id)?.name)
        .filter((n): n is string => Boolean(n))
    : null
  const daysLeft = periods ? daysLeftInWeek(periods.write.week_start) : null

  // ONE weekly card, randomly chosen among this week's weeklies the
  // user has NOT entered, re-rolled every time the tab mounts. All
  // three entered (or offline) = no card at all. Guests read all
  // three as unentered, so they always get a card.
  const [weeklyPick, setWeeklyPick] = useState<WeeklyCardOption | 'none' | null>(null)
  useEffect(() => {
    if (!configured) { setWeeklyPick('none'); return }
    let cancelled = false
    fetchWeeklyCardOptions()
      .then(rows => {
        if (cancelled) return
        const open = rows.filter(r => !r.entered)
        setWeeklyPick(open.length === 0
          ? 'none'
          : open[Math.floor(Math.random() * open.length)])
      })
      .catch(() => { if (!cancelled) setWeeklyPick('none') })
    return () => { cancelled = true }
  }, [configured])

  // Living-rulebook opener for the cards in LIVING_RULES: resolves
  // the game through the rules API on each tap, so re-published books
  // on the site are live here immediately.
  const [livingRules, setLivingRules] = useState<{
    slug: string; title: string; jsonUrl: string; locales: string[]; accent: string
  } | null>(null)
  const [livingBusyId, setLivingBusyId] = useState<string | null>(null)
  async function openLivingRules(cardId: string) {
    const target = LIVING_RULES[cardId]
    if (!target || livingBusyId) return
    setLivingBusyId(cardId)
    try {
      const idx = await fetchRulesIndex()
      const game = idx.games.find(g => g.slug === target.slug)
      if (game?.livingRulebook) {
        setLivingRules({
          slug: game.slug,
          title: game.title,
          jsonUrl: game.livingRulebook.json,
          locales: game.livingRulebook.locales,
          accent: target.accent,
        })
      }
    } catch { /* offline — the pill just doesn't open this tap */ }
    finally { setLivingBusyId(null) }
  }

  if (livingRules) {
    return (
      <LivingRulebookScreen
        slug={livingRules.slug}
        title={livingRules.title}
        jsonUrl={livingRules.jsonUrl}
        locales={livingRules.locales}
        accent={livingRules.accent}
        onBack={() => setLivingRules(null)}
      />
    )
  }

  return (
    <div className="screen pb-20">
      <BackgroundTitle text="Games" color="#D9E026" />
      <ScreenHeader
        title={t('GAMES')}
        titleColorClass="text-brand-lime-dark"
        profile={profile}
        onProfileTap={() => onNavigate('profile')}
      />

      <div className="px-5 space-y-5 pb-6 relative z-10">
        {/* Weekly Event Banner — one card, random among the weeklies
            you have NOT entered this week; hidden once all three are
            in. The pick reshuffles on every tab landing. */}
        {weeklyPick !== null && weeklyPick !== 'none' && (() => {
          const pick = weeklyPick
          // Same fill, stroke, and image treatment as the game cards
          // in the grid below, so the weekly banner reads as "that
          // game's card, promoted" rather than a third visual style.
          const META = {
            iet: {
              img: '/games/ideatthat.webp', alt: "I'D EAT THAT",
              accent: 'text-game-iet', title: t("This Week's I'D EAT THAT"),
              fill: 'iet-gold', stroke: 'border-game-iet-dark',
              pill: '',
            },
            colorful: {
              img: '/games/colorful.webp', alt: 'Colorful',
              accent: 'text-game-colorful-dark', title: t("This Week's Colorful"),
              fill: 'bg-game-colorful-light', stroke: 'border-game-colorful-dark',
              pill: 'text-game-colorful-dark bg-game-colorful-dark/10',
            },
            three_step: {
              img: '/games/three-step.webp', alt: 'THREE STEP SOLUTION',
              accent: 'text-game-three-step-dark', title: t("This Week's THREE STEP SOLUTION"),
              fill: 'bg-game-three-step-light', stroke: 'border-game-three-step-dark',
              pill: 'text-game-three-step-dark bg-game-three-step-dark/10',
            },
          }[pick.game]
          const line =
            pick.game === 'iet'
              ? (sharedNames && sharedNames.length > 0
                  ? t('{list} · +1 random', { list: sharedNames.join(' · ') })
                  : t('Loading this week’s ingredients…'))
            : pick.game === 'colorful'
              ? (pick.subjects && pick.subjects.length > 0
                  ? `${pick.subjects.slice(0, 3).join(' · ')}${pick.subjects.length > 3 ? t(' · +{n} more', { n: pick.subjects.length - 3 }) : ''}`
                  : t('Six subjects, one color each.'))
              : t('How do you {a} while {b} at the same time?', { a: pick.action_a ?? '', b: pick.action_b ?? '' })
          return (
            <button
              onClick={() => onWeeklyEvent(pick.game)}
              className={`w-full ${META.fill} border-2 ${META.stroke} rounded-2xl shadow-card
                          p-4 text-left active:scale-[0.98] transition-transform`}
            >
              <div className="flex items-start gap-4">
                <img src={META.img} alt={META.alt}
                     className="w-14 h-14 rounded-2xl object-cover shadow-xs shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <span className={`text-xs font-semibold ${META.accent} uppercase tracking-wide`}>
                      {t('Weekly Event')}
                    </span>
                    <div className={`countdown-pill shrink-0 ${META.pill}`}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                      {daysLeft !== null ? t('{n}d left', { n: daysLeft }) : t('… left')}
                    </div>
                  </div>
                  <h2 className="font-display text-lg font-bold text-brand-text">
                    {t(META.title)}
                  </h2>
                  <p className="text-sm text-brand-muted mt-0.5">{line}</p>
                </div>
              </div>
            </button>
          )
        })()}

        {/* Join a host — neutral white card. Per the house rule, white
            cards use a grey stroke + the standard `shadow-card`. */}
        <button
          onClick={() => { window.location.href = '/join' }}
          className="w-full bg-white rounded-2xl shadow-card p-4 flex items-center gap-3 text-left
                     border-2 border-brand-border
                     active:scale-[0.98] transition-transform"
        >
          <div className="w-12 h-12 rounded-2xl bg-brand-elevated flex items-center justify-center shrink-0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M21 10h-8.35A5.99 5.99 0 007 6c-3.31 0-6 2.69-6 6s2.69 6 6 6a5.99 5.99 0 005.65-4H13l2 2 2-2 2 2 2-2v-4z"
                    fill="#111"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-brand-text">{t('Join a host')}</h3>
            <p className="text-xs text-brand-muted">
              {t("Got a 6-character room code? Jump into a friend's game.")}
            </p>
          </div>
          <ChevronRight size={20} color="#6B7280" className="shrink-0" />
        </button>

        {/* Jump back in — running rooms this account is seated in.
            Tapping reuses the push-notification path (requestRoomNav →
            App validates the room is still live → enters it), so this
            list and the push can never route differently. */}
        {activeRooms.length > 0 && (
          <div>
            <SectionTitle className="mb-3">{t('Jump back in')}</SectionTitle>
            {/* At most 4 tiles here (my-turn rooms sort first, so the
                cut never hides a room that's waiting on you ahead of
                one that isn't). The rest live behind the pill. */}
            <div className="space-y-3">
              {activeRooms.slice(0, 4).map(r => (
                <ActiveRoomCard key={`${r.game}:${r.room_id}`} room={r}
                                onLeft={() => setRoomsTick(t => t + 1)} />
              ))}
            </div>
            {activeRooms.length > 4 && (
              <button
                type="button"
                onClick={onOpenMyRooms}
                className="mt-3 w-full flex items-center justify-center gap-2 py-3
                           rounded-full border-2 border-brand-text bg-white
                           font-display font-bold text-sm tracking-widest text-brand-text
                           active:scale-[0.98] transition-transform"
              >
                {t('YOUR GAME ROOMS')}
                <span className="min-w-6 h-6 px-1.5 rounded-full bg-brand-text text-white
                                 text-xs font-bold flex items-center justify-center">
                  {activeRooms.length}
                </span>
              </button>
            )}
          </div>
        )}

        {/* Games Grid */}
        <div>
          <SectionTitle className="mb-3">{t('Games')}</SectionTitle>
          {/* stagger-children + animate-slide-up = each card slides up
              ~60ms after the previous, so the list cascades in instead
              of materialising all at once. */}
          <div className="space-y-3 stagger-children">
            {GAMES.map(game => {
              const playable = game.available && Boolean(handlers[game.id])
              return (
                <div
                  key={game.id}
                  className={`w-full ${game.fillClass} border-2 ${game.strokeClass}
                              rounded-2xl shadow-card p-4 flex items-center gap-4
                              transition-all duration-150 animate-slide-up
                              ${playable ? '' : 'opacity-60'}`}
                >
                  <button
                    type="button"
                    onClick={playable ? handlers[game.id] : undefined}
                    disabled={!playable}
                    className={`flex-1 flex items-center gap-4 text-left
                                ${playable ? 'active:scale-[0.98] cursor-pointer' : 'cursor-default'}`}
                  >
                    {game.image ? (
                      <img
                        src={game.image}
                        alt={game.title}
                        className="w-14 h-14 rounded-2xl object-cover shadow-xs shrink-0"
                      />
                    ) : (
                      <div
                        aria-hidden="true"
                        className={`w-14 h-14 rounded-2xl shadow-xs shrink-0
                                    bg-white/60 border-2 ${game.strokeClass}`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {game.wordmark ? (
                          <GameTitle game={game.wordmark} size="sm" />
                        ) : (
                          <h3 className={`font-display font-black text-lg tracking-[0.06em] uppercase
                                          ${game.titleClass ?? 'text-brand-text'}`}>
                            {game.title}
                          </h3>
                        )}
                        {!game.available && (
                          <span className="text-[10px] font-semibold text-brand-muted
                                           bg-brand-elevated px-2 py-0.5 rounded-full border border-brand-border
                                           tracking-wide">
                            {t('COMING SOON')}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-brand-muted">{t(game.tagline)}</p>
                    </div>
                  </button>
                  {/* Rules — separate tap target so opening the rulebook
                      doesn't also kick off a play action. Always live,
                      even for COMING SOON games. Three Step Solution
                      doesn't have a rulebook PDF yet — hide the button
                      for that game until one ships. */}
                  {LIVING_RULES[game.id] && (
                    <button
                      type="button"
                      onClick={() => { void openLivingRules(game.id) }}
                      disabled={livingBusyId === game.id}
                      className="shrink-0 px-3 py-1.5 rounded-full bg-white/70 text-xs
                                 font-semibold text-brand-text border border-brand-border
                                 active:scale-95 transition-transform disabled:opacity-60"
                    >
                      {livingBusyId === game.id ? t('Loading…') : t('Rules')}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Subscription CTA (if not subscribed). Opens the full
            All-Access paywall — that's where price tiles, the 3-day
            trial, and the StoreKit / Stripe handoff all live. */}
        {profile.subscription_status !== 'active' && (
          <button
            type="button"
            onClick={onOpenPaywall}
            className="card w-full p-5 border-2 border-brand-gold/30 bg-brand-gold/5
                       text-left active:scale-[0.99] transition-transform"
          >
            <div className="flex items-center gap-3">
              <img src="/jdg-logo.webp" alt="JDG" className="w-10 h-10 shrink-0" />
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-brand-text">{t('Get All-Access')}</h3>
                <p className="text-sm text-brand-muted mt-0.5">
                  {t('Every game, every week. 3-day free trial, cancel anytime.')}
                </p>
              </div>
              <ChevronRight />
            </div>
          </button>
        )}
      </div>

    </div>
  )
}

